/**
 * Sidebar navigation items for the User Management module.
 * Provides navigation to users, roles, approvals, and audit.
 * Navigation items are feature-flag gated.
 */
import { ManageAccountsRounded, ManageAccountsTwoTone } from '@mui/icons-material';

import { useHasPermission } from '@/userManagement/application/adapters/auth/useHasPermission';
import { TOOL_ACCESS_POLICIES } from '@/userManagement/domain/constants/toolAccessPermissionPolicies';
import { SETTINGS_PERMISSION_POLICIES } from '@/userManagement/domain/constants/settingsPermissionPolicies';
import { BULK_UPLOAD_POLICIES } from '@/userManagement/domain/constants/bulkUploadPermissionPolicies';
import { ACCESS_REVIEW_POLICIES } from '@/userManagement/domain/constants/accessReviewPermissionPolicies';
import { USER_MANAGEMENT_FEATURE_FLAGS } from '@/userManagement/domain/constants/featureFlags';
import {
  USER_MANAGEMENT_ACCESS_REVIEW_SUBMODULE,
  USER_MANAGEMENT_BULK_UPLOAD_SUBMODULE,
  USER_MANAGEMENT_SETTINGS_SUBMODULE,
  USER_MANAGEMENT_TOOL_ACCESS_SUBMODULE
} from '@/userManagement/domain/constants/userManagementSubmodules';
import { NavItemType } from '@/common/presentation/components/layout/Sidebar/type';
import { useFilterNavItemsByFeatureFlags } from '@/common/presentation/utils/filterNavItems';
import { useFeatureFlag } from '@/common/presentation/hooks/useFeatureFlag';

export interface UserManagementNavItemsResult {
  item: NavItemType | null;
  isReady: boolean;
}

const UserManagementNavItems = (): UserManagementNavItemsResult => {
  const canManageToolAccess = useHasPermission(TOOL_ACCESS_POLICIES.TOOL_ADMINISTRATION);
  const canEditSettings = useHasPermission(SETTINGS_PERMISSION_POLICIES.WRITE);
  const canReadSettingsJwt = useHasPermission(SETTINGS_PERMISSION_POLICIES.READ);
  const canViewSettings = canEditSettings || canReadSettingsJwt || canManageToolAccess;
  const canUseBulkUpload = useHasPermission(BULK_UPLOAD_POLICIES.BULK_UPLOAD);
  const accessReviewFlagEnabled = useFeatureFlag(USER_MANAGEMENT_FEATURE_FLAGS.USER_MANAGEMENT_QUARTERLY_REVIEW);
  const canReadAccessReview = useHasPermission(ACCESS_REVIEW_POLICIES.READ);
  const showAccessReviewTab = accessReviewFlagEnabled && canReadAccessReview;

  const children = [
    {
      id: 'user_management_users',
      title: 'Users',
      type: 'item' as const,
      url: '/user-management?module=user-management&submodule=user_management_users'
    },
    {
      id: 'user_management_roles',
      title: 'Roles',
      type: 'item' as const,
      url: '/user-management?module=user-management&submodule=user_management_roles',
      featureFlag: USER_MANAGEMENT_FEATURE_FLAGS.USER_MANAGEMENT_ROLES
    },
    {
      id: 'user_management_approvals',
      title: 'Approvals',
      type: 'item' as const,
      url: '/user-management?module=user-management&submodule=user_management_approvals',
      featureFlag: USER_MANAGEMENT_FEATURE_FLAGS.USER_MANAGEMENT_APPROVALS
    },
    {
      id: 'user_management_audit',
      title: 'Audit Log',
      type: 'item' as const,
      url: '/user-management?module=user-management&submodule=user_management_audit',
      featureFlag: USER_MANAGEMENT_FEATURE_FLAGS.USER_MANAGEMENT_AUDIT
    },
    ...(showAccessReviewTab
      ? [
          {
            id: USER_MANAGEMENT_ACCESS_REVIEW_SUBMODULE,
            title: 'Access Review',
            type: 'item' as const,
            url: `/user-management?module=user-management&submodule=${USER_MANAGEMENT_ACCESS_REVIEW_SUBMODULE}`
          }
        ]
      : []),
    ...(canViewSettings
      ? [
          {
            id: USER_MANAGEMENT_SETTINGS_SUBMODULE,
            title: 'Settings',
            type: 'item' as const,
            url: `/user-management?module=user-management&submodule=${USER_MANAGEMENT_SETTINGS_SUBMODULE}`
          }
        ]
      : []),
    ...(canManageToolAccess
      ? [
          {
            id: USER_MANAGEMENT_TOOL_ACCESS_SUBMODULE,
            title: 'Tool Access',
            type: 'item' as const,
            url: `/user-management?module=user-management&submodule=${USER_MANAGEMENT_TOOL_ACCESS_SUBMODULE}`
          }
        ]
      : []),
    ...(canUseBulkUpload
      ? [
          {
            id: USER_MANAGEMENT_BULK_UPLOAD_SUBMODULE,
            title: 'Bulk Role Edit',
            type: 'item' as const,
            url: `/user-management?module=user-management&submodule=${USER_MANAGEMENT_BULK_UPLOAD_SUBMODULE}`
          }
        ]
      : [])
  ];

  const navItems: NavItemType = {
    id: 'user_management',
    title: 'User Management',
    type: 'collapse',
    icon: ManageAccountsTwoTone,
    iconSelected: ManageAccountsRounded,
    url: '/user-management?module=user-management',
    featureFlag: USER_MANAGEMENT_FEATURE_FLAGS.USER_MANAGEMENT_PAGE,
    children
  };

  const { items: filteredItems, isReady } = useFilterNavItemsByFeatureFlags([navItems]);
  return {
    item: filteredItems[0] ?? null,
    isReady
  };
};

export default UserManagementNavItems;
